import { makeStyles, alpha } from '@material-ui/core/styles';
// Shared design tokens: type scale, text colours and clickable-card behaviour.
import {
  cardTransition,
  eyebrowStyle,
  headingColor,
  icon,
  linkCardStyle,
  linkColor,
  text,
  tintTileStyle,
} from '@univerus/plugin-products';
// The hero stays brand navy in both colour modes, so it uses brand colours.
import { brand } from '../../themes/universusTheme';

// Hero display sizes: a deliberate exception to the page type scale.
const HERO_TITLE = { regular: 48, phone: 36 };

// One shortcut row: icon, then label and detail. Used by the launchers under
// the hero and the Employee tools list; each sets its own columns.
const SHORTCUT_ROW = {
  display: 'grid',
  alignItems: 'center',
  gap: 14,
  padding: '14px 16px',
};

export const useHomeStyles = makeStyles(theme => {
  const white = theme.palette.common.white;
  return {
    page: {
      maxWidth: 1360,
      margin: '0 auto',
      padding: theme.spacing(3, 4, 8),
      width: '100%',
      boxSizing: 'border-box',
      color: theme.palette.text.primary,
      minWidth: 0,
      '& a:focus-visible, & button:focus-visible': {
        outline: `3px solid ${theme.palette.primary.main}`,
        outlineOffset: 4,
      },
      [theme.breakpoints.down('xs')]: { padding: theme.spacing(1.5, 2, 4) },
    },

    // Hero: brand navy panel with search inside it, like a docs landing page.
    hero: {
      position: 'relative',
      boxSizing: 'border-box',
      padding: '56px 56px 96px',
      borderRadius: theme.shape.borderRadius * 3,
      backgroundColor: brand.navy,
      // The overlay stays solid behind the text block (px), then fades out.
      backgroundImage: `linear-gradient(90deg, ${alpha(
        brand.navyDeep,
        0.97,
      )} 0px, ${alpha(brand.navy, 0.88)} 640px, ${alpha(
        brand.navy,
        0.1,
      )} 100%), url(/home/technology-hero.jpg)`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      // Light focus rings on navy (the page default is too dark here).
      '& a:focus-visible, & button:focus-visible': { outlineColor: brand.mist },
      [theme.breakpoints.down('sm')]: {
        backgroundImage: `linear-gradient(${alpha(
          brand.navyDeep,
          0.92,
        )}, ${alpha(brand.navy, 0.92)}), url(/home/technology-hero.jpg)`,
      },
      [theme.breakpoints.down('xs')]: { padding: '32px 20px 40px' },
    },
    heroIntro: { minWidth: 0, maxWidth: 580 },
    eyebrow: {
      display: 'block',
      fontSize: text.label,
      fontWeight: 700,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: brand.teal,
      marginBottom: 16,
    },
    title: {
      fontSize: HERO_TITLE.regular,
      fontWeight: 700,
      lineHeight: 1.06,
      letterSpacing: '-1.4px',
      color: white,
      [theme.breakpoints.down('xs')]: { fontSize: HERO_TITLE.phone },
    },
    titleAccent: { display: 'block', color: brand.teal },
    heroDescription: {
      color: brand.mist,
      fontSize: text.emphasis,
      lineHeight: '25px',
      margin: '16px 0 24px',
      maxWidth: 440,
    },
    heroSearch: { maxWidth: 560 },
    // Search field: styled through the text field's public class props.
    search: {
      width: '100%',
      minHeight: 52,
      borderRadius: theme.shape.borderRadius * 2,
      background: white,
      color: brand.navyDeep,
      boxShadow: `0 10px 30px ${alpha(brand.navyDeep, 0.35)}`,
      '& input': { fontSize: text.lead, color: brand.navyDeep },
      '& input::placeholder': {
        color: alpha(brand.navyDeep, 0.55),
        opacity: 1,
      },
      '& svg': { color: alpha(brand.navyDeep, 0.6) },
      // The Clear button sits on white in both colour modes.
      '& button': { color: brand.navyDeep },
    },
    searchFocused: { boxShadow: `0 0 0 3px ${brand.mist}` },
    searchOutline: { border: 0 },
    visuallyHidden: {
      position: 'absolute',
      width: 1,
      height: 1,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
    },
    heroActions: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 16,
      marginTop: 16,
    },
    suggestions: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 8,
      fontSize: text.meta,
      '& > span': { color: alpha(brand.mist, 0.75), marginRight: 4 },
      '& a': {
        color: white,
        padding: '4px 12px',
        borderRadius: 999,
        background: alpha(white, 0.1),
        border: `1px solid ${alpha(white, 0.16)}`,
        transition: 'background-color 150ms ease',
      },
      '& a:hover': { textDecoration: 'none', background: alpha(white, 0.2) },
    },
    // Launchers overlapping the hero's bottom edge.
    startHere: {
      position: 'relative',
      zIndex: 1,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
      gap: 16,
      margin: '-40px 32px 0',
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: '1fr',
        margin: '24px 0 0',
      },
    },
    tileIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 36,
      height: 36,
      flexShrink: 0,
      ...tintTileStyle(theme),
      '& svg': { fontSize: icon.medium },
    },
    tileText: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
    tileLabel: {
      fontSize: text.lead,
      fontWeight: 600,
      lineHeight: '20px',
      letterSpacing: '-0.1px',
      color: headingColor(theme),
    },
    tileDetail: {
      fontSize: text.small,
      lineHeight: '19px',
      color: theme.palette.text.secondary,
    },
    tileArrow: {
      fontSize: text.emphasis,
      fontWeight: 600,
      color: linkColor(theme),
    },
    // Compact launchers: short and wide with an icon, so they read as
    // navigation rather than as content cards like Recently viewed below.
    startCard: {
      ...linkCardStyle(theme),
      ...SHORTCUT_ROW,
      color: theme.palette.text.primary,
      boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.08)}`,
      // Icon and label only: side by side, arrows read as a sequence.
      gridTemplateColumns: '36px minmax(0, 1fr)',
    },
    // Launchers grow slightly on rollover instead of showing an arrow. A
    // separate class, so it adds to the card's lift rather than replacing it;
    // the scale property composes with the lift's transform.
    launcherGrow: {
      transition: `${cardTransition}, scale 150ms ease`,
      '&:focus-visible': { scale: '1.03' },
      '@media (hover: hover)': { '&:hover': { scale: '1.03' } },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&:hover, &:focus-visible': { scale: 'none' },
      },
    },
    toolRow: {
      ...SHORTCUT_ROW,
      // Icon, label and detail, arrow.
      gridTemplateColumns: '36px minmax(0, 1fr) auto',
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.palette.divider}`,
      transition: 'background-color 150ms ease',
      '&:last-child': { borderBottom: 0 },
      '&:hover, &:focus-visible': {
        textDecoration: 'none',
        background: alpha(theme.palette.primary.main, 0.06),
      },
      '&:focus-visible': {
        outline: `2px solid ${linkColor(theme)}`,
        outlineOffset: -3,
      },
      '& $tileArrow': { transition: 'transform 150ms ease' },
      '&:hover $tileArrow, &:focus-visible $tileArrow': {
        transform: 'translate(2px, -2px)',
      },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '& $tileArrow': { transition: 'none' },
        '&:hover $tileArrow, &:focus-visible $tileArrow': { transform: 'none' },
      },
    },

    // Section headings use the shared SectionIntro; this row holds it and links.
    section: { marginTop: 0, minWidth: 0 },
    sectionHead: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 8,
      marginBottom: 20,
      // A heading's links, alone or grouped in sectionLinks.
      '& > a, & > nav > a': {
        fontSize: text.small,
        fontWeight: 600,
        color: linkColor(theme),
      },
    },
    sectionLinks: { display: 'flex', flexWrap: 'wrap', gap: 16 },
    muted: {
      color: theme.palette.text.secondary,
      fontWeight: 400,
      fontSize: text.meta,
      lineHeight: '18px',
    },
    resources: { minWidth: 0 },
    tools: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius * 2,
      background: theme.palette.background.paper,
      overflow: 'hidden',
    },

    recentSection: {
      margin: '56px 32px 0',
      [theme.breakpoints.down('xs')]: { margin: '32px 0 0' },
    },
    recent: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: 16,
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
    },
    recentLink: {
      ...linkCardStyle(theme),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 8,
      minHeight: 128,
      padding: 20,
      color: theme.palette.text.primary,
      minWidth: 0,
      '& strong': {
        fontWeight: 600,
        fontSize: text.title,
        lineHeight: '24px',
        letterSpacing: '-0.2px',
        overflowWrap: 'anywhere',
        color: headingColor(theme),
      },
      '& svg': { fontSize: icon.small, flexShrink: 0 },
    },
    cardEyebrow: eyebrowStyle(theme),
    recentDescription: {
      fontSize: text.small,
      lineHeight: '20px',
      color: theme.palette.text.secondary,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    cardFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 'auto',
      paddingTop: 12,
      fontSize: text.small,
      fontWeight: 600,
      color: linkColor(theme),
    },
    onboarding: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '8px 20px',
      marginTop: 20,
      fontSize: text.small,
      color: theme.palette.text.secondary,
      '& a': { fontWeight: 600, color: linkColor(theme) },
    },
    lower: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)',
      gap: 48,
      margin: '56px 32px 0',
      paddingTop: 48,
      borderTop: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr', gap: 40 },
      [theme.breakpoints.down('xs')]: { margin: '32px 0 0', paddingTop: 32 },
    },
    newsCard: {
      ...linkCardStyle(theme),
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    // The card's own corners and border frame the image.
    newsImage: {
      aspectRatio: '1.6',
      overflow: 'hidden',
      background: alpha(theme.palette.primary.main, 0.12),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.palette.text.secondary,
      fontSize: text.meta,
      letterSpacing: '3px',
      '& img': { width: '100%', height: '100%', objectFit: 'cover' },
    },
    newsBody: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      gap: 8,
      padding: 20,
    },
    postDescription: {
      fontSize: text.small,
      lineHeight: '21px',
      color: theme.palette.text.secondary,
      margin: '8px 0 0',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    feed: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius * 2,
      padding: '0 20px',
    },
    feedRow: {
      display: 'grid',
      gridTemplateColumns: '80px minmax(0, 1fr)',
      gap: 16,
      padding: '20px 0',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': { borderBottom: 0 },
    },
    feedTitle: {
      fontSize: text.title,
      fontWeight: 600,
      lineHeight: '24px',
      margin: '0 0 6px',
      overflowWrap: 'anywhere',
      color: headingColor(theme),
    },
    tabs: {
      '& [role="tablist"]': { flexWrap: 'wrap', gap: 8 },
      minHeight: 36,
      marginBottom: 24,
      '& button': {
        minHeight: 34,
        minWidth: 64,
        padding: '6px 14px',
        fontSize: text.small,
        borderRadius: 999,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        textTransform: 'none',
      },
      '& [aria-selected="true"]': {
        color: linkColor(theme),
        borderColor: alpha(theme.palette.primary.main, 0.55),
        background: alpha(theme.palette.primary.main, 0.1),
        fontWeight: 600,
      },
    },
    // Passed to Tabs as classes.indicator: the pills carry the selected state.
    tabIndicatorHidden: { display: 'none' },
    feedFooter: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      gap: 8,
      marginTop: 24,
      fontSize: text.meta,
    },
    empty: {
      padding: 24,
      background: theme.palette.background.paper,
      border: `1px dashed ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius * 2,
      fontSize: text.body,
      color: theme.palette.text.secondary,
    },
    releasesSection: {
      margin: '56px 32px 0',
      paddingTop: 48,
      borderTop: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down('xs')]: { margin: '32px 0 0', paddingTop: 32 },
    },
    releaseFilters: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 2fr) repeat(2, minmax(0, 1fr))',
      gap: 12,
      marginBottom: 16,
      [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
    },
    community: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)',
      gap: 48,
      margin: '56px 32px 0',
      paddingTop: 48,
      borderTop: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr', gap: 40 },
      [theme.breakpoints.down('xs')]: { margin: '32px 0 0', paddingTop: 32 },
    },
    previewNote: {
      fontSize: text.meta,
      color: theme.palette.text.secondary,
      margin: '-8px 0 16px',
      lineHeight: '18px',
    },
    scrollControls: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: 8,
      marginBottom: 12,
      '& > span': {
        marginRight: 'auto',
        fontSize: text.meta,
        color: theme.palette.text.secondary,
      },
      '& button': {
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.primary.main,
      },
      // aria-disabled rather than disabled, so a button keeps keyboard focus
      // when it reaches the end of the track.
      '& button[aria-disabled="true"]': {
        color: theme.palette.action.disabled,
        cursor: 'default',
        '&:hover': { background: 'none' },
      },
    },
    // A sideways track that snaps card by card. overflow-x clips the other
    // axis too, so the track keeps room for a card's lift and shadow and
    // gives it back with negative margins.
    scrollTrack: {
      display: 'grid',
      gridAutoFlow: 'column',
      gap: 24,
      overflowX: 'auto',
      overscrollBehaviorX: 'contain',
      scrollSnapType: 'x mandatory',
      // A string: JSS adds no px unit to scroll-padding.
      scrollPaddingLeft: '6px',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      padding: '6px 6px 24px',
      margin: '-6px -6px -18px',
      '& > *': { scrollSnapAlign: 'start' },
    },
    // Three releases across the full width.
    releaseTrack: {
      gridAutoColumns: 'calc((100% - 48px) / 3)',
      [theme.breakpoints.down('sm')]: {
        gridAutoColumns: 'calc((100% - 24px) / 2)',
      },
      [theme.breakpoints.down('xs')]: { gridAutoColumns: '88%', gap: 16 },
    },
    // Two posts and the edge of a third, which says there is more.
    newsTrack: {
      gridAutoColumns: 'calc((100% - 48px) / 2.15)',
      // Narrow beside Resources: one post and most of the next.
      [theme.breakpoints.only('md')]: { gridAutoColumns: '72%' },
      [theme.breakpoints.down('xs')]: { gridAutoColumns: '85%', gap: 16 },
    },
    releaseCard: {
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: theme.spacing(1, 0, 2),
    },
    releaseActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      width: '100%',
      marginTop: 'auto',
    },
    releasePortfolio: {
      ...eyebrowStyle(theme),
      marginBottom: theme.spacing(0.75),
    },
    releaseProduct: {
      fontSize: text.title,
      fontWeight: 600,
      lineHeight: 1.4,
      marginBottom: theme.spacing(1),
      color: headingColor(theme),
    },
    releaseSummary: {
      fontSize: text.body,
      lineHeight: 1.65,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
      '& strong': { color: theme.palette.text.primary, fontWeight: 600 },
    },
    releaseReadMore: {
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: 44,
      marginTop: 'auto',
      fontSize: text.body,
      fontWeight: 600,
      color: linkColor(theme),
      '& svg': { fontSize: icon.small },
    },
    recognitionCards: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 16,
      [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
    },
    recognitionCard: {
      display: 'flex',
      flexDirection: 'column',
      padding: 20,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius * 2,
      background: theme.palette.background.paper,
      '& p': { fontSize: text.small, lineHeight: '20px', margin: '14px 0 8px' },
    },
    recognitionByline: {
      fontSize: text.meta,
      lineHeight: '17px',
      '& > span': {
        display: 'block',
        color: theme.palette.text.secondary,
        fontSize: text.label,
      },
    },
    recognitionPeople: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      marginTop: 12,
      '& > span': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: alpha(theme.palette.primary.main, 0.12),
        color: linkColor(theme),
      },
      '& b': { fontSize: icon.medium },
      '& > svg': { fontSize: text.lead, color: theme.palette.text.secondary },
    },
    recognitionReaction: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      marginTop: 'auto',
      fontSize: text.label,
      color: theme.palette.text.secondary,
      '& svg': { fontSize: icon.compact },
    },
    recognitionActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 16,
      '& > button': {
        borderRadius: 999,
        fontSize: text.meta,
        padding: '4px 18px',
      },
    },
    poll: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius * 2,
      overflow: 'hidden',
      background: theme.palette.background.paper,
    },
    pollHeader: {
      padding: '12px 20px',
      background: alpha(theme.palette.primary.main, 0.1),
      color: linkColor(theme),
      fontSize: text.meta,
      fontWeight: 600,
    },
    // Passed to FormControlLabel as classes.label.
    pollLabel: { fontSize: text.small },
    pollBody: {
      padding: 20,
      '& > button': { marginTop: 12 },
    },
    skip: {
      position: 'absolute',
      left: -10000,
      '&:focus': {
        position: 'fixed',
        left: 16,
        top: 16,
        zIndex: 9999,
        padding: 12,
        background: theme.palette.background.paper,
      },
    },
  };
});
